import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import AuthLayout from '../../../shared/components/layout/AuthLayout';
import { useFriends } from '../hooks/useFriends';
import { UserSearch } from '../components/UserSearch';
import { ConnectModal } from '../components/ConnectModal';
import { PendingRequests } from '../components/PendingRequests';
import { FriendCard } from '../components/FriendCard';
import { FriendSearchResult, FriendshipTier } from '../types/friends';
import { FriendsListSkeleton } from '../components/FriendsListSkeleton';
import { ReconnectingChip } from '../../../shared/components/ui/ReconnectingChip';

// Time window options matching portfolio feature
const TIME_WINDOW_OPTIONS = [
  { key: '1m', label: '1 month' },
  { key: '3m', label: '3 months' },
  { key: '6m', label: '6 months' },
  { key: '1y', label: '1 year' },
  { key: '2y', label: '2 years' },
];

// Same localStorage key as portfolio page
const LOCALSTORAGE_SELECTED_TIME_WINDOW = 'dashboard_selectedTimeWindowKey';

export const FriendsPage: React.FC = () => {
  const navigate = useNavigate();

  // Load time window from localStorage (same as portfolio page)
  const [timeWindow, setTimeWindow] = useState(() => {
    try {
      const saved = localStorage.getItem(LOCALSTORAGE_SELECTED_TIME_WINDOW);
      if (saved) {
        const found = TIME_WINDOW_OPTIONS.find(option => option.key === saved);
        if (found) {
          return found.key;
        }
      }
    } catch (error) {
      console.error('Error loading time window from localStorage:', error);
    }
    return '6m'; // Default to 6 months (matching portfolio default)
  });
  
  const [isTimeWindowOpen, setIsTimeWindowOpen] = useState(false);
  const timeWindowRef = useRef<HTMLDivElement>(null);

  // Save to localStorage when changed (sync with portfolio page)
  useEffect(() => {
    try {
      localStorage.setItem(LOCALSTORAGE_SELECTED_TIME_WINDOW, timeWindow);
    } catch (error) {
      console.error('Error saving time window to localStorage:', error);
    }
  }, [timeWindow]);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (timeWindowRef.current && !timeWindowRef.current.contains(event.target as Node)) {
        setIsTimeWindowOpen(false);
      }
    };

    if (isTimeWindowOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isTimeWindowOpen]);
  const {
    friends,
    incomingRequests,
    outgoingRequests,
    searchResults,
    searchQuery,
    setSearchQuery,
    loading,
    searchLoading,
    browseLoading,
    error,
    loadBrowseResults,
    sendRequest,
    respondToRequest,
    unfriend,
    updateTier,
    requestHigherTier,
    refresh
  } = useFriends(timeWindow);

  const [activeTab, setActiveTab] = useState<'friends' | 'requests' | 'search'>('friends');
  const [connectModalOpen, setConnectModalOpen] = useState(false);
  const [selectedUser, setSelectedUser] = useState<FriendSearchResult | null>(null);

  const handleConnectClick = (user: FriendSearchResult) => {
    setSelectedUser(user);
    setConnectModalOpen(true);
  };

  const handleConnectSubmit = async (tier: FriendshipTier, note?: string) => {
    if (selectedUser) {
      await sendRequest(selectedUser.userId, tier, note);
    }
  };

  const handleSearchEnter = () => {
    if (searchQuery.length >= 2) {
      setActiveTab('search');
    }
  };

  const handleTabChange = (tab: 'friends' | 'requests' | 'search') => {
    setActiveTab(tab);
    // When switching to search tab without a query, load browse results
    if (tab === 'search' && (!searchQuery || searchQuery.length < 2)) {
      loadBrowseResults();
    }
  };

  const pendingCount = incomingRequests.length;

  const selectedTimeWindowLabel = TIME_WINDOW_OPTIONS.find(opt => opt.key === timeWindow)?.label || '1 month';

  return (
    <AuthLayout>
      <div className="space-y-3">
        {/* Header with Search Bar and Time Window Selector */}
        <div className="flex items-center justify-between gap-3 -mt-4 mb-2">
          {/* Search Bar - Set width with ability to shrink */}
          <div className="w-[28rem] min-w-0 shrink max-w-full">
            <UserSearch
              searchQuery={searchQuery}
              setSearchQuery={setSearchQuery}
              searchResults={searchResults}
              isLoading={searchLoading}
              onConnect={handleConnectClick}
              onEnterPress={handleSearchEnter}
              onUserClick={(user) => navigate(`/friends/${user.userId}`)}
            />
          </div>
          
          {/* Time Window Selector */}
          <div className="relative flex-shrink-0" ref={timeWindowRef}>
            <button
              onClick={() => setIsTimeWindowOpen(!isTimeWindowOpen)}
              className="flex items-center space-x-2 px-4 py-2 bg-blue-500 text-white rounded-full text-sm hover:bg-blue-600 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2"
            >
              <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
              <span>{selectedTimeWindowLabel}</span>
            </button>

            {/* Dropdown */}
            {isTimeWindowOpen && (
              <div className="origin-top-right absolute right-0 mt-2 w-32 rounded-md shadow-lg bg-white ring-1 ring-black ring-opacity-5 focus:outline-none z-10 top-full">
                <div className="py-1" role="menu" aria-orientation="vertical">
                  {TIME_WINDOW_OPTIONS.map((option) => (
                    <button
                      key={option.key}
                      onClick={() => {
                        setTimeWindow(option.key);
                        setIsTimeWindowOpen(false);
                      }}
                      className={`block w-full text-left px-4 py-2 text-sm ${
                        timeWindow === option.key
                          ? 'bg-gray-100 text-gray-900'
                          : 'text-gray-700'
                      } hover:bg-gray-100 hover:text-gray-900`}
                      role="menuitem"
                    >
                      {option.label}
                    </button>
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Tabs */}
        <div className="border-b border-gray-200 mb-6">
          <nav className="-mb-px flex space-x-8" aria-label="Tabs">
            <button
              onClick={() => handleTabChange('friends')}
              className={`
                whitespace-nowrap py-4 px-1 border-b-2 font-medium text-sm
                ${activeTab === 'friends'
                  ? 'border-blue-500 text-blue-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'}
              `}
            >
              Friends ({friends.length})
            </button>
            <button
              onClick={() => handleTabChange('requests')}
              className={`
                whitespace-nowrap py-4 px-1 border-b-2 font-medium text-sm flex items-center
                ${activeTab === 'requests'
                  ? 'border-blue-500 text-blue-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'}
              `}
            >
              Requests
              {pendingCount > 0 && (
                <span className="ml-2 bg-red-100 text-red-600 py-0.5 px-2 rounded-full text-xs">
                  {pendingCount}
                </span>
              )}
            </button>
            <button
              onClick={() => handleTabChange('search')}
              className={`
                whitespace-nowrap py-4 px-1 border-b-2 font-medium text-sm
                ${activeTab === 'search'
                  ? 'border-blue-500 text-blue-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'}
              `}
            >
              Search
            </button>
          </nav>
        </div>

        {/* Content */}
        {loading && friends.length === 0 ? (
          // Only show full-screen spinner on initial load (no cached data)
          <FriendsListSkeleton />
        ) : (
          <>
            {/* Show error chip if there's an error but we have cached data */}
            {error && friends.length > 0 && (
              <ReconnectingChip message={error} onRetry={refresh} />
            )}
            
            {activeTab === 'friends' && (
              <div>
                {friends.length === 0 && !loading ? (
                  // Only show empty state if we successfully fetched and got 0 results
                  <div className="text-center py-12 bg-gray-50 rounded-lg border border-gray-200 border-dashed">
                    <p className="text-gray-500">You haven't added any friends yet.</p>
                    <p className="text-sm text-gray-400 mt-1">Search for people above to connect!</p>
                  </div>
                ) : friends.length > 0 ? (
                  <div className="space-y-3">
                    {friends.map((friend) => (
                      <FriendCard 
                        key={friend.friendshipId} 
                        friend={friend} 
                        onUnfriend={unfriend}
                        onUpdateTier={updateTier}
                        onRequestHigherTier={requestHigherTier}
                        timeWindowLabel={selectedTimeWindowLabel}
                      />
                    ))}
                  </div>
                ) : (
                  // Still loading but no cached data - show skeleton
                  <FriendsListSkeleton />
                )}
              </div>
            )}

            {activeTab === 'requests' && (
              <PendingRequests
                incomingRequests={incomingRequests}
                outgoingRequests={outgoingRequests}
                onRespond={respondToRequest}
              />
            )}

            {activeTab === 'search' && (
              <div>
                {(searchLoading || browseLoading) ? (
                  <div className="flex justify-center py-12">
                    <svg className="animate-spin h-8 w-8 text-blue-500" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                    </svg>
                  </div>
                ) : searchResults.length === 0 ? (
                  <div className="text-center py-12 bg-gray-50 rounded-lg border border-gray-200 border-dashed">
                    {searchQuery && searchQuery.length >= 2 ? (
                      <p className="text-gray-500">No users found matching "{searchQuery}"</p>
                    ) : (
                      <p className="text-gray-500">No users found</p>
                    )}
                  </div>
                ) : (
                  <div className="space-y-3">
                    {searchResults.map((user) => (
                      <div
                        key={user.userId}
                        role="button"
                        tabIndex={0}
                        onClick={() => navigate(`/friends/${user.userId}`)}
                        onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); navigate(`/friends/${user.userId}`); } }}
                        className={`flex items-center justify-between px-4 py-3 bg-white rounded-lg border border-gray-200 hover:border-gray-300 transition-colors cursor-pointer ${user.isFriend ? 'opacity-60' : ''}`}
                      >
                        <div className="flex items-center min-w-0 flex-1">
                          <div className="flex-shrink-0 h-10 w-10">
                            {user.profilePictureUrl ? (
                              <img className={`h-10 w-10 rounded-full object-cover ${user.isFriend ? 'grayscale' : ''}`} src={user.profilePictureUrl} alt="" />
                            ) : (
                              <div className={`h-10 w-10 rounded-full bg-gray-200 flex items-center justify-center font-medium text-lg ${user.isFriend ? 'text-gray-400' : 'text-gray-500'}`}>
                                {user.name.charAt(0).toUpperCase()}
                              </div>
                            )}
                          </div>
                          <div className="ml-3 min-w-0 flex-1">
                            <p className={`text-sm font-medium truncate ${user.isFriend ? 'text-gray-500' : 'text-gray-900'}`}>{user.name}</p>
                            <p className={`text-sm truncate ${user.isFriend ? 'text-gray-400' : 'text-gray-500'}`}>@{user.username || 'unknown'}</p>
                          </div>
                        </div>
                        <div className="ml-4 flex-shrink-0">
                          {user.isFriend ? (
                            <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
                              Friend
                            </span>
                          ) : user.hasOutgoingRequest ? (
                            <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-yellow-100 text-yellow-800">
                              Pending
                            </span>
                          ) : user.hasIncomingRequest ? (
                            <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                              Requested
                            </span>
                          ) : (
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                handleConnectClick(user);
                              }}
                              className="inline-flex items-center px-3 py-1.5 border border-transparent text-xs font-medium rounded-full shadow-sm text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                            >
                              Connect
                            </button>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}
          </>
        )}

        {/* Modals */}
        {selectedUser && (
          <ConnectModal
            isOpen={connectModalOpen}
            onClose={() => {
              setConnectModalOpen(false);
              setSelectedUser(null);
            }}
            onConnect={handleConnectSubmit}
            recipientName={selectedUser.name}
          />
        )}
      </div>
    </AuthLayout>
  );
};
